import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User, Lock, Link2, Camera } from "lucide-react";
import { supabase } from "../../lib/supabase";
import {
  getProfile,
  upsertProfile,
  uploadAvatar,
  getOnboardingDraft,
  clearOnboardingDraft,
  type ProfileUpdate,
} from "../../lib/profiles";
import { useMembreAccess } from "../../hooks/useMembreAccess";
import { useDossierStore } from "../../store/dossierStore";

const FONT = "'IBM Plex Sans', system-ui, sans-serif";
const AVATAR_MAX = 2 * 1024 * 1024;
const AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"];

function initialesAvatar(prenom: string, nom: string, email: string): string {
  const p = prenom.trim();
  const n = nom.trim();
  if (p && n) return (p[0] + n[0]).toUpperCase();
  if (p) return p.slice(0, 2).toUpperCase();
  if (n) return n.slice(0, 2).toUpperCase();
  const local = (email.split("@")[0] ?? "").trim();
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  return "?";
}

function libelleProfil(prenom: string, nom: string, email: string): string {
  const p = prenom.trim();
  const n = nom.trim();
  if (p && n) return `${p} ${n}`;
  if (p) return p;
  if (n) return n;
  return email || "Utilisateur";
}

const TABS = [
  { id: "informations", path: "/compte/informations", label: "Mes informations", icon: User },
  { id: "mot-de-passe", path: "/compte/mot-de-passe", label: "Mon mot de passe", icon: Lock },
  { id: "connexions-externes", path: "/compte/connexions-externes", label: "Connexions externes", icon: Link2 },
] as const;

function TabContent({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ marginTop: "24px" }}>
      {children}
    </div>
  );
}

const COUNTRIES = [
  { code: "CI", label: "Côte d'Ivoire" },
  { code: "SN", label: "Sénégal" },
  { code: "CM", label: "Cameroun" },
];

const STRUCTURE_OPTIONS = [
  { value: "grosse", label: "Grosse Entreprise (Normal)" },
  { value: "petite", label: "Petite Entreprise (SMT)" },
  { value: "independant", label: "Indépendant" },
];

function MesInformations() {
  const { loading: membreAccessLoading, role } = useMembreAccess();
  const { activeDossier } = useDossierStore();
  /** Propriétaire explicite, ou hors tableau membres (role null) : accès complet. Comptable / DAF : masqué. */
  const showEntrepriseFiscalite = role === "owner" || role === null;

  const fileRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  const [savedAvatarUrl, setSavedAvatarUrl] = useState<string | null>(null);
  const [pendingAvatar, setPendingAvatar] = useState<File | null>(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [avatarImgError, setAvatarImgError] = useState(false);
  const [form, setForm] = useState<ProfileUpdate & { email?: string }>({
    prenom: "",
    nom: "",
    telephone: "",
    entreprise: "",
    pays: "",
    structure: null,
    tva_recup: null,
  });

  useEffect(() => {
    return () => {
      if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
    };
  }, [avatarPreviewUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled || !user) return;
      setUserId(user.id);
      setEmail(user.email ?? "");
      try {
        const profile = await getProfile(user.id);
        const draft = getOnboardingDraft();
        const fromDraft = draft && !profile;
        const source = fromDraft ? draft : profile;
        setSavedAvatarUrl((profile?.avatar_url as string | undefined)?.trim() || null);
        setForm({
          prenom: (source?.prenom ?? profile?.prenom ?? "") || "",
          nom: (source?.nom ?? profile?.nom ?? "") || "",
          telephone: (source?.telephone ?? profile?.telephone ?? "") || "",
          entreprise: (source?.entreprise ?? profile?.entreprise ?? "") || "",
          pays: (source?.pays ?? profile?.pays ?? "") || "",
          structure: source?.structure ?? profile?.structure ?? null,
          tva_recup: source?.tva_recup ?? profile?.tva_recup ?? null,
        });
      } catch (e) {
        console.error(e);
        setMessage({ type: "error", text: "Impossible de charger le profil." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onPickAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!AVATAR_TYPES.includes(f.type)) {
      setMessage({ type: "error", text: "Formats acceptés : JPG, PNG ou WebP." });
      return;
    }
    if (f.size > AVATAR_MAX) {
      setMessage({ type: "error", text: "Image trop lourde (maximum 2 Mo)." });
      return;
    }
    setMessage(null);
    setAvatarPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    setPendingAvatar(f);
    setAvatarImgError(false);
  };

  const clearPendingAvatar = () => {
    setPendingAvatar(null);
    setAvatarPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    setMessage(null);
    setSaving(true);
    try {
      let nextAvatarUrl: string | null = savedAvatarUrl;
      if (pendingAvatar) {
        nextAvatarUrl = await uploadAvatar(userId, pendingAvatar);
      }
      const profilCommun = {
        prenom: form.prenom || null,
        nom: form.nom || null,
        telephone: form.telephone || null,
        avatar_url: nextAvatarUrl,
      };
      await upsertProfile(
        userId,
        showEntrepriseFiscalite
          ? {
              ...profilCommun,
              entreprise: form.entreprise || null,
              pays: form.pays || null,
              structure: form.structure || null,
              tva_recup: form.tva_recup || null,
            }
          : profilCommun
      );
      if (pendingAvatar) clearPendingAvatar();
      setSavedAvatarUrl(nextAvatarUrl);
      setAvatarImgError(false);
      clearOnboardingDraft();
      setMessage({ type: "success", text: "Vos informations ont été enregistrées." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Erreur lors de l’enregistrement.",
      });
    } finally {
      setSaving(false);
    }
  };

  const prenom = form.prenom ?? "";
  const nom = form.nom ?? "";
  const showImg =
    !!avatarPreviewUrl || (!!savedAvatarUrl && !avatarImgError);

  if (loading || (Boolean(activeDossier?.id) && membreAccessLoading)) {
    return (
      <TabContent>
        <div
          style={{
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "16px",
            padding: "32px 40px",
            fontFamily: FONT,
          }}
        >
          <p style={{ fontSize: "0.9rem", color: "#64748b" }}>Chargement...</p>
        </div>
      </TabContent>
    );
  }

  return (
    <TabContent>
      {/* En-tête profil */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 24,
          padding: "28px 32px",
          marginBottom: 20,
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          border: "1px solid #e2e8f0",
          borderRadius: "20px",
          boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
          fontFamily: FONT,
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={onPickAvatar}
        />
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: "50%",
            overflow: "hidden",
            flexShrink: 0,
            background: "linear-gradient(145deg, #fff7ed, #ffedd5)",
            border: "3px solid rgba(249,115,22,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {showImg ? (
            <img
              src={avatarPreviewUrl || savedAvatarUrl || ""}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={() => setAvatarImgError(true)}
            />
          ) : (
            <span style={{ fontSize: 28, fontWeight: 800, color: "#ea580c" }}>
              {initialesAvatar(prenom, nom, email)}
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: "0 0 6px 0",
              fontSize: "1.35rem",
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.02em",
            }}
          >
            {libelleProfil(prenom, nom, email)}
          </h3>
          {email ? (
            <p style={{ margin: "0 0 14px 0", fontSize: "0.875rem", color: "#64748b" }}>{email}</p>
          ) : null}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 16px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                background: "white",
                color: "#475569",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: "pointer",
                fontFamily: FONT,
              }}
            >
              <Camera size={18} strokeWidth={2} color="#f97316" />
              {pendingAvatar ? "Changer la photo" : "Photo de profil"}
            </button>
            {pendingAvatar ? (
              <button
                type="button"
                onClick={clearPendingAvatar}
                style={{
                  padding: "10px 14px",
                  borderRadius: 12,
                  border: "none",
                  background: "transparent",
                  color: "#64748b",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: FONT,
                }}
              >
                Annuler la nouvelle photo
              </button>
            ) : null}
          </div>
          {pendingAvatar ? (
            <p style={{ margin: "10px 0 0 0", fontSize: "0.78rem", color: "#f97316", fontWeight: 600 }}>
              Aperçu : la photo sera enregistrée lorsque vous cliquerez sur « Enregistrer ».
            </p>
          ) : (
            <p style={{ margin: "10px 0 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>
              JPG, PNG ou WebP · max 2 Mo
            </p>
          )}
        </div>
      </div>

      <div
        style={{
          background: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "16px",
          padding: "32px 40px",
          fontFamily: FONT,
        }}
      >
        <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a", marginBottom: "8px" }}>
          Coordonnées
        </h3>
        <p style={{ fontSize: "0.8rem", color: "#64748b", marginBottom: "24px" }}>
          {showEntrepriseFiscalite
            ? "Ces informations sont visibles par les membres de vos dossiers (ex. commentaires)."
            : "Vos coordonnées sont visibles par les membres de vos dossiers (ex. commentaires)."}
        </p>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <label style={labelStyle}>Prénom</label>
            <input
              type="text"
              value={form.prenom ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, prenom: e.target.value }))}
              placeholder="Prénom"
              style={inputStyle}
              disabled={saving}
            />
          </div>
          <div>
            <label style={labelStyle}>Nom</label>
            <input
              type="text"
              value={form.nom ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
              placeholder="Nom"
              style={inputStyle}
              disabled={saving}
            />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={email}
              readOnly
              style={{ ...inputStyle, background: "#f1f5f9", cursor: "not-allowed", color: "#475569" }}
            />
            <p style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: "6px" }}>
              L’adresse e-mail est liée à votre compte de connexion (non modifiable ici).
            </p>
          </div>
          <div>
            <label style={labelStyle}>Téléphone</label>
            <input
              type="tel"
              value={form.telephone ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, telephone: e.target.value }))}
              placeholder="+225 07 00 00 00 00"
              style={inputStyle}
              disabled={saving}
            />
          </div>

          {showEntrepriseFiscalite ? (
            <>
              <div style={{ marginTop: 8, paddingTop: 24, borderTop: "1px solid #f1f5f9" }}>
                <h4 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0f172a", margin: "0 0 16px 0" }}>
                  Entreprise et fiscalité
                </h4>
              </div>
              <div>
                <label style={labelStyle}>Nom de l’entreprise</label>
                <input
                  type="text"
                  value={form.entreprise ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, entreprise: e.target.value }))}
                  placeholder="Ex: Ma Société SARL"
                  style={inputStyle}
                  disabled={saving}
                />
              </div>
              <div>
                <label style={labelStyle}>Pays d’activité</label>
                <select
                  value={form.pays ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, pays: e.target.value || null }))}
                  style={{ ...inputStyle, cursor: "pointer" }}
                  disabled={saving}
                >
                  <option value="">Choisir un pays</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Taille de la structure</label>
                <select
                  value={form.structure ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      structure: (e.target.value || null) as "grosse" | "petite" | "independant" | null,
                    }))
                  }
                  style={{ ...inputStyle, cursor: "pointer" }}
                  disabled={saving}
                >
                  <option value="">Choisir</option>
                  {STRUCTURE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Récupérez-vous la TVA sur vos achats ?</label>
                <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="tva_recup"
                      checked={form.tva_recup === "oui"}
                      onChange={() => setForm((f) => ({ ...f, tva_recup: "oui" }))}
                      disabled={saving}
                    />
                    <span style={{ fontSize: "0.9rem" }}>Oui</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="tva_recup"
                      checked={form.tva_recup === "non"}
                      onChange={() => setForm((f) => ({ ...f, tva_recup: "non" }))}
                      disabled={saving}
                    />
                    <span style={{ fontSize: "0.9rem" }}>Non</span>
                  </label>
                </div>
              </div>
            </>
          ) : null}

          {message && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "10px",
                fontSize: "0.9rem",
                background: message.type === "success" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                color: message.type === "success" ? "#16A34A" : "#DC2626",
              }}
            >
              {message.text}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            style={{
              padding: "12px 24px",
              borderRadius: "12px",
              border: "none",
              background: saving ? "#94a3b8" : "linear-gradient(135deg, #F97316, #EAB308)",
              color: "#0f172a",
              fontWeight: 700,
              fontSize: "0.9rem",
              fontFamily: FONT,
              cursor: saving ? "not-allowed" : "pointer",
              alignSelf: "flex-start",
            }}
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </form>
      </div>
    </TabContent>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 420,
  padding: "12px 16px",
  fontSize: "0.9rem",
  fontFamily: FONT,
  color: "#0f172a",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  outline: "none",
};
const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "0.8rem",
  fontWeight: 600,
  color: "#475569",
  marginBottom: "8px",
  fontFamily: FONT,
};

function MonMotDePasse() {
  const [ancienMdp, setAncienMdp] = useState("");
  const [nouveauMdp, setNouveauMdp] = useState("");
  const [confirmerMdp, setConfirmerMdp] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email ?? null);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!ancienMdp || !nouveauMdp || !confirmerMdp) {
      setMessage({ type: "error", text: "Tous les champs sont obligatoires." });
      return;
    }
    if (nouveauMdp !== confirmerMdp) {
      setMessage({ type: "error", text: "Le nouveau mot de passe et la confirmation ne correspondent pas." });
      return;
    }
    if (nouveauMdp.length < 6) {
      setMessage({ type: "error", text: "Le nouveau mot de passe doit contenir au moins 6 caractères." });
      return;
    }
    if (!userEmail) {
      setMessage({ type: "error", text: "Impossible de récupérer votre adresse email." });
      return;
    }

    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password: ancienMdp,
      });
      if (signInError) {
        if (signInError.message.includes("Invalid") || signInError.message.includes("invalid")) {
          setMessage({ type: "error", text: "Ancien mot de passe incorrect." });
        } else {
          setMessage({ type: "error", text: signInError.message });
        }
        setLoading(false);
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({ password: nouveauMdp });
      if (updateError) {
        setMessage({ type: "error", text: updateError.message });
        setLoading(false);
        return;
      }

      setMessage({ type: "success", text: "Votre mot de passe a été modifié avec succès." });
      setAncienMdp("");
      setNouveauMdp("");
      setConfirmerMdp("");
    } catch (err) {
      setMessage({ type: "error", text: err instanceof Error ? err.message : "Une erreur est survenue." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TabContent>
      <div
        style={{
          background: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "16px",
          padding: "32px 40px",
          fontFamily: FONT,
        }}
      >
        <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a", marginBottom: "24px" }}>
          Changer mon mot de passe
        </h3>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <label style={labelStyle}>Ancien mot de passe</label>
            <input
              type="password"
              value={ancienMdp}
              onChange={(e) => setAncienMdp(e.target.value)}
              placeholder="Entrez votre mot de passe actuel"
              style={inputStyle}
              disabled={loading}
              autoComplete="current-password"
            />
          </div>
          <div>
            <label style={labelStyle}>Nouveau mot de passe</label>
            <input
              type="password"
              value={nouveauMdp}
              onChange={(e) => setNouveauMdp(e.target.value)}
              placeholder="Entrez votre nouveau mot de passe"
              style={inputStyle}
              disabled={loading}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label style={labelStyle}>Confirmer le nouveau mot de passe</label>
            <input
              type="password"
              value={confirmerMdp}
              onChange={(e) => setConfirmerMdp(e.target.value)}
              placeholder="Confirmez votre nouveau mot de passe"
              style={inputStyle}
              disabled={loading}
              autoComplete="new-password"
            />
          </div>

          {message && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "10px",
                fontSize: "0.9rem",
                background: message.type === "success" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                color: message.type === "success" ? "#16A34A" : "#DC2626",
              }}
            >
              {message.text}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              padding: "12px 24px",
              borderRadius: "12px",
              border: "none",
              background: loading ? "#C4B8A8" : "linear-gradient(135deg, #F97316, #EAB308)",
              color: "#0A0A0A",
              fontWeight: 700,
              fontSize: "0.9rem",
              fontFamily: FONT,
              cursor: loading ? "not-allowed" : "pointer",
              alignSelf: "flex-start",
            }}
          >
            {loading ? "Chargement..." : "Valider"}
          </button>
        </form>
      </div>
    </TabContent>
  );
}

function ConnexionsExternes() {
  return (
    <TabContent>
      <div
        style={{
          background: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "16px",
          padding: "32px 40px",
          fontFamily: FONT,
        }}
      >
        <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a", marginBottom: "20px" }}>
          Connexions externes
        </h3>
        <p style={{ fontSize: "0.9rem", color: "#64748b", lineHeight: 1.6 }}>
          Contenu à développer : liaisons avec Google, etc.
        </p>
      </div>
    </TabContent>
  );
}

export default function MonCompte() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/compte" || location.pathname === "/compte/") {
      navigate("/compte/informations", { replace: true });
    } else if (location.pathname.startsWith("/compte") && !["/compte/informations", "/compte/mot-de-passe", "/compte/connexions-externes"].includes(location.pathname)) {
      navigate("/compte/informations", { replace: true });
    }
  }, [location.pathname, navigate]);

  const renderContent = () => {
    if (location.pathname === "/compte/informations") return <MesInformations />;
    if (location.pathname === "/compte/mot-de-passe") return <MonMotDePasse />;
    if (location.pathname === "/compte/connexions-externes") return <ConnexionsExternes />;
    return null;
  };

  return (
    <div style={{ fontFamily: FONT, maxWidth: 820 }}>
      <div style={{ marginBottom: "8px" }}>
        <h2 style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
          Gérer mon compte
        </h2>
        <p style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "6px" }}>
          Gérez vos informations personnelles et la sécurité de votre compte
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: "4px",
          background: "#f1f5f9",
          padding: "4px",
          borderRadius: "14px",
          border: "1px solid #e2e8f0",
          marginBottom: "4px",
        }}
      >
        {TABS.map((tab) => {
          const isActive = location.pathname === tab.path;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 18px",
                borderRadius: "10px",
                border: "none",
                background: isActive ? "white" : "transparent",
                color: isActive ? "#0f172a" : "#64748b",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: "pointer",
                fontFamily: FONT,
                boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
              }}
            >
              <Icon size={18} strokeWidth={2} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {renderContent()}
    </div>
  );
}
